import { useEffect, useMemo, useRef, useState } from 'react'

import { Icons } from 'atoms'
import { useSupportAnalyticsMutation } from 'integration/resources/supportAnalytics'
import {
  Box,
  Button,
  HStack,
  Pressable,
  Text,
  TextArea,
  VStack,
  useBreakpointValue,
  useToast
} from 'native-base'
import { Toast as ToastMessage } from 'organisms'
import { Animated, Easing, StyleSheet } from 'react-native'

const SupportWidget = () => {
  const toast = useToast()

  const [isOpen, setIsOpen] = useState(false)

  const [contentVisible, setContentVisible] = useState(false)

  const [description, setDescription] = useState('')

  const [descriptionCount, setDescriptionCount] = useState(0)

  const [descriptionError, setDescriptionError] = useState<string | undefined>(undefined)

  const [isSuccess, setIsSuccess] = useState(false)

  const openProgress = useRef(new Animated.Value(0)).current

  const { mutate, isLoading } = useSupportAnalyticsMutation()

  const resetState = () => {
    setDescription('')

    setDescriptionCount(0)

    setDescriptionError(undefined)

    setIsSuccess(false)
  }

  const toggleOpen = () => {
    if (isOpen) {
      resetState()
    }

    setIsOpen((prev) => !prev)
  }

  const handleClose = () => {
    setIsOpen(false)

    resetState()
  }

  const handleSubmit = () => {
    const trimmedDescription = description.trim()

    if (trimmedDescription.length < 5) {
      setDescriptionError('Descreva o problema com pelo menos 5 caracteres.')

      return
    }

    mutate(
      { description: trimmedDescription },
      {
        onSuccess: () => {
          setIsSuccess(true)

          setDescription('')

          setDescriptionCount(0)

          setDescriptionError(undefined)
        },
        onError: () => {
          toast.show({
            render: () => (
              <ToastMessage type="error" text="Não conseguimos enviar agora, tente novamente." />
            ),
            duration: 5000
          })
        }
      }
    )
  }

  const isSubmitDisabled = useMemo(() => {
    const length = description.trim().length

    return length < 5 || isLoading
  }, [description, isLoading])

  useEffect(() => {
    if (isOpen) {
      setContentVisible(true)
    }

    Animated.timing(openProgress, {
      toValue: isOpen ? 1 : 0,
      duration: 220,
      easing: Easing.out(Easing.ease),
      useNativeDriver: false
    }).start(({ finished }) => {
      if (!isOpen && finished) {
        setContentVisible(false)
      }
    })
  }, [isOpen, openProgress])

  const collapsedWidth = useBreakpointValue({ base: 236 }) ?? 236

  const expandedWidth = useBreakpointValue({ base: 360 }) ?? 440

  const expandedHeight = useBreakpointValue({ base: 420 }) ?? 480

  const AnimatedBox = useMemo(() => Animated.createAnimatedComponent(Box), [])

  const containerAnimatedStyle = useMemo(
    () => ({
      width: openProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [collapsedWidth, expandedWidth]
      }),
      height: openProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [48, expandedHeight]
      })
    }),
    [collapsedWidth, expandedHeight, expandedWidth, openProgress]
  )

  const drawerStyle = useMemo(
    () => ({
      opacity: openProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1]
      }),
      maxHeight: openProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, expandedHeight]
      }),
      transform: [
        {
          translateY: openProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [80, 0]
          })
        },
        {
          scale: openProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0.98, 1]
          })
        }
      ]
    }),
    [expandedHeight, openProgress]
  )

  return (
    <AnimatedBox
      position="absolute"
      bottom={0}
      left={{ base: '16px', md: '24px', xl: '32px' }}
      borderTopRadius="8px"
      overflow="hidden"
      bg="transparent"
      alignSelf="flex-start"
      style={[styles.container, containerAnimatedStyle]}>
      <Pressable
        onPress={toggleOpen}
        bg="primary.500"
        px={5}
        py={3}
        justifyContent="center"
        _hover={{ bg: 'primary.500' }}>
        <HStack alignItems="center" justifyContent="center" space={3}>
          <Icons.Support color="white" size={5} />
          <Text fontSize="16px" fontWeight="semibold" color="white">
            Reportar Divergência
          </Text>
        </HStack>
      </Pressable>

      {contentVisible && (
        <Animated.View style={[styles.content, drawerStyle]}>
          <VStack
            bg="gray.70"
            px={{ base: 4, md: 5 }}
            pt={{ base: 3.5, md: 4 }}
            pb={{ base: 10, md: 12 }}
            flex={1}
            justifyContent="space-between"
            borderBottomRadius="18px">
            {isSuccess ? (
              <VStack flex={1} space={6} justifyContent="center">
                <Text textAlign="center" fontSize="16px" lineHeight="22px" color="gray.700">
                  Recebemos sua solicitação e nossa equipe já está analisando o caso. Você receberá
                  uma atualização por e-mail assim que o problema for solucionado.
                </Text>
                <Button
                  variant="outline"
                  borderColor="gray.700"
                  borderWidth="1.5px"
                  borderRadius="full"
                  onPress={handleClose}
                  h={{ base: 11, md: 12 }}
                  _text={{
                    color: 'gray.800',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                  Ok, entendi.
                </Button>
              </VStack>
            ) : (
              <VStack space={5} flex={1}>
                <VStack space={2}>
                  <Text fontSize="16px" fontWeight="normal" color="gray.700">
                    Problema
                  </Text>
                  <TextArea
                    autoCompleteType={null}
                    value={description}
                    onChangeText={(value) => {
                      const nextValue = value.slice(0, 2000)

                      setDescription(nextValue)

                      const trimmedLength = nextValue.trim().length

                      setDescriptionCount(trimmedLength)

                      if (trimmedLength >= 5 && descriptionError) {
                        setDescriptionError(undefined)
                      }
                    }}
                    isInvalid={!!descriptionError}
                    placeholder="Digite aqui"
                    placeholderTextColor="#A8A8A8"
                    bgColor="white"
                    minH="120px"
                    borderRadius="14px"
                    borderColor={descriptionError ? 'error.500' : 'transparent'}
                    _focus={{
                      borderColor: descriptionError ? 'error.500' : 'primary.500',
                      bgColor: 'white'
                    }}
                    fontSize="16px"
                    lineHeight="22px"
                    color="gray.800"
                    p={4}
                    maxLength={2000}
                  />
                  <HStack justifyContent="flex-end">
                    <Text fontSize="12px" color="gray.500">
                      {descriptionCount}/2000
                    </Text>
                  </HStack>
                  {descriptionError && (
                    <Text fontSize="12px" color="error.500">
                      {descriptionError}
                    </Text>
                  )}
                </VStack>

                <Button
                  onPress={handleSubmit}
                  isDisabled={isSubmitDisabled}
                  isLoading={isLoading}
                  bg="primary.500"
                  borderRadius="full"
                  h={{ base: 11, md: 12 }}
                  _text={{ fontWeight: 'bold', fontSize: '16px', color: 'white' }}
                  _hover={{ bg: 'primary.500' }}>
                  Enviar
                </Button>
              </VStack>
            )}
          </VStack>
        </Animated.View>
      )}
    </AnimatedBox>
  )
}

const styles = StyleSheet.create({
  container: {
    zIndex: 10,
    elevation: 12,
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12
  },
  content: {
    flex: 1,
    width: '100%'
  }
})

export default SupportWidget
